import { PageWrapper } from '../../components/PageWrapper';
import { useNavigate, useLocation } from 'react-router-dom';
import { PHONE_PASSWORD_LOGIN, PHONE_PASSWORD_CREATE } from '../../constants/routes';
import { useState, useEffect } from 'react';
import OtpInput from 'react18-input-otp';
import { handleOtpVerification, handleForgotPasswordFlow } from '../../utils/phoneNumber';
import { auth } from '../../firebase/firebaseConfig';
import { formatPhoneNumber } from '../../utils/phoneNumber';
import useAxios from '../../axios/useAxios';
import { BeatLoader } from 'react-spinners';
export const PhoneForgotPasswordScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { phoneNumber, userExists } = location.state || {};
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const axiosInstance = useAxios();

  // Redirect if no phone number
  useEffect(() => {
    if (!phoneNumber) {
      navigate(PHONE_PASSWORD_LOGIN);
    }
  }, [phoneNumber, navigate]);

  // Check if confirmationResult exists
  useEffect(() => {
    if (!window.confirmationResult) {
      setError('Session expirée. Veuillez recommencer.');
      setTimeout(() => {
        navigate(PHONE_PASSWORD_LOGIN);
      }, 2000);
    }
  }, [navigate]);

  const displayNumber = phoneNumber ? formatPhoneNumber(phoneNumber) : '';

  // Handle OTP verification
  const handleVerifyCode = async (otp) => {
    if (otp.length !== 6) return;

    setError('');

    const result = await handleOtpVerification({
      auth,
      otp,
      setLoading,
      onSuccess: () => {
        // Navigate to password reset screen
        // Only pass serializable data (not the full Firebase user object)
        // isNewUser is false/undefined for forgot password (existing user)
        navigate(PHONE_PASSWORD_CREATE, {
          state: {
            phoneNumber,
            isForgotPassword: true,
            isVerified: true,
            isNewUser: false, // Explicitly false for existing users
          },
        });
      },
      onError: (errorMessage) => {
        console.error('OTP verification failed:', errorMessage);

        // Handle specific errors
        if (errorMessage?.includes('code-expired')) {
          setError('Code expiré. Veuillez demander un nouveau code.');
          setTimeout(() => {
            navigate(PHONE_PASSWORD_LOGIN);
          }, 3000);
        } else if (errorMessage?.includes('invalid-verification-code')) {
          setError('Code incorrect. Veuillez réessayer.');
        } else {
          setError('Erreur de vérification. Veuillez réessayer.');
        }

        setCode('');
      },
    });

    if (!result.success) {
      setCode('');
    }
  };

  // Auto-verify when code is complete
  useEffect(() => {
    if (code.length === 6) {
      handleVerifyCode(code);
    }
  }, [code]);

  const handleResendSMS = async () => {
    if (!phoneNumber || resending) return;

    setResending(true);
    setError('');
    setCode('');

    const result = await handleForgotPasswordFlow({
      phoneNumber,
      auth,
      axiosInstance,
      setLoading: setResending,
      recaptchaContainerId: 'forgot-password-resend-recaptcha',
    });

    if (result.success) {
      setResending(false);
    } else {
      setResending(false);
    }
  };

  return (
    <PageWrapper title="👀 Code envoyé par SMS" onGoBack={() => navigate(PHONE_PASSWORD_LOGIN, { state: { phoneNumber: phoneNumber, userExists } })}>
      <div className="flex flex-col items-center justify-center gap-4">
        <p className="text-start w-full text-xl text-gray-800 geomanistRegular mb-6">
          Envoyé au <span className="geomanistMedium">{displayNumber}</span>
        </p>
        {loading ? (
          <BeatLoader color="#183153" size={20} />
        ) : (
          <OtpInput
            value={code}
            onChange={setCode}
            numInputs={6}
            isInputNum={true}
            isDisabled={loading}
            shouldAutoFocus={true}
            inputStyle="password-input"
            isInputSecure={false}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            separator={<span className="otp-separator" />}
            containerStyle="otp-container"
          />
        )}

        {loading && <p className="text-gray-500 text-lg mt-4">Vérification...</p>}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4 w-full">
            <p className="text-red-600 text-lg font-semibold text-center">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <p className="text-gray-500 text-xl mt-4 text-center">
            Le code expire dans quelques minutes
          </p>
        )}

        <button
          type="button"
          className="geomanistMedium mt-6 text-xl p-4 underline rounded-full w-full disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleResendSMS}
          disabled={resending || loading}
        >
          {resending ? 'Envoi en cours...' : 'Renvoyer le SMS'}
        </button>
      </div>
      {/* Hidden reCAPTCHA container for resend */}
      <div id="forgot-password-resend-recaptcha" style={{ display: 'none' }} />
    </PageWrapper>
  );
};